import api from "../utils/axiosConfig";

/**
 * The account states the API actually has.
 *
 * This used to read `"active" | "inactive" | "suspended"`, none of which the
 * server has ever sent — the mapping below quietly folded the real value into
 * `active`/`inactive`, so the type was describing the shim rather than the API,
 * and the reason an account could not bet was thrown away on the way in.
 *
 * Only `active` may place a bet. The other three are refused by the API and
 * disabled in the UI, and each wants a different sentence said to the player.
 */
export type PlayerStatus = "pending_review" | "active" | "banned" | "rejected";

export interface ProfileData {
  user_id: string;
  name: string;
  nationality: string;
  ic_or_passport: string;
  phone_no: string;
  status: PlayerStatus;
  created_at: string;
  wallet?: {
    balance: number;
  };
}

export interface GetProfileResponse {
  status: number;
  system: string;
  message: string;
  data: ProfileData;
}

export type ApiProfile = {
  id: string;
  username: string;
  status: PlayerStatus;
  balanceCents: number;
  createdAt: string;
};

export const toProfileResponse = (profile: ApiProfile): GetProfileResponse => ({
  status: 1,
  system: "profile",
  message: "Profile retrieved",
  data: {
    user_id: profile.id,
    name: profile.username,
    nationality: "",
    ic_or_passport: "",
    phone_no: "",
    // Passed through as sent. Flattening it here is what left the bet screen
    // unable to say *why* it was disabled.
    status: profile.status,
    created_at: profile.createdAt,
    wallet: { balance: profile.balanceCents / 100 },
  },
});

export const getProfile = async (): Promise<GetProfileResponse> => {
  try {
    const response = await api.get<ApiProfile>("/me");
    return toProfileResponse(response.data);
  } catch (error: any) {
    console.error("Get profile error:", error);
    throw error;
  }
};

/** Only an approved account may bet. Every other state is refused by the API. */
export const canPlaceBets = (status: PlayerStatus | null | undefined) =>
  status === "active";
